import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from "../environments/environment";
import { PermissionService } from './permission.service';
import { User, CurrentUser } from '../_models/user.model';

interface LoginResponse {
  success: boolean;
  token: string;
  currentUser: User;
  message?: string;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private isLoggingOut = false;

  constructor(
      private http: HttpClient,
      private router: Router,
      private permissionService: PermissionService
    ) { }

  login(email: string, loginCode?: string) {
    if (loginCode) {
      return this.http.post<LoginResponse>(`${environment.apiUrl}/users/authorize`, {email, loginCode});
    } else {
      return this.http.post<LoginResponse>(`${environment.apiUrl}/users/authorize`, {email});
    }
  }

  logout(): boolean {
    // Prevent multiple simultaneous logout attempts
    // Return false if logout is already in progress
    if (this.isLoggingOut) {
      console.log('AuthService: Logout already in progress, skipping');
      return false;
    }
    
    // Set flag immediately to prevent race conditions
    // Flag will only be reset on successful login
    this.isLoggingOut = true;
    console.log('AuthService: Starting logout process');
    
    localStorage.removeItem('currentUser');
    localStorage.removeItem('token');
    
    // Clear impersonation state if any
    localStorage.removeItem('isImpersonating');
    localStorage.removeItem('originalAdminData');
    localStorage.removeItem('impersonatedUserData');
    
    // Clear global reporting entity filter
    localStorage.removeItem('globalReportingEntityFilter');
    
    this.permissionService.clearPermissions();
    
    this.router.navigate(['/login']).then(() => {
      console.log('AuthService: Navigation to login complete');
    });
    
    return true;
  }

  isLoggingOutInProgress(): boolean {
    return this.isLoggingOut;
  }

  setCurrentUser(userInfo: CurrentUser, token?: string): void {
    // Reset logout flag when user logs in successfully
    this.isLoggingOut = false;
    console.log('AuthService: User logged in, logout flag reset');
    
    // If token is provided separately, attach it to the user object
    if (token) {
      userInfo.token = token;
      localStorage.setItem('token', token);
    }
    
    localStorage.setItem('currentUser', JSON.stringify(userInfo));
    
    // Load permissions after setting user
    this.permissionService.loadPermissions().subscribe({
      next: () => {
        console.log('User permissions loaded successfully');
      },
      error: (error) => {
        console.error('Error loading user permissions:', error);
      }
    });
  }

  getCurrentUser(): CurrentUser | null {
    const userStr = localStorage.getItem('currentUser');
    if (userStr) {
      return JSON.parse(userStr);
    }
    return null;
  }

  isAuthenticated(): boolean {
    const user = this.getCurrentUser();
    return user !== null;
  }

  initializeAuth(): Promise<boolean> {
    return new Promise((resolve) => {
      const user = this.getCurrentUser();
      if (user) {
        console.log('Authentication state restored from storage');
        resolve(true);
      } else {
        console.log('No existing authentication state found');
        resolve(false);
      }
    });
  }

  hasRole(role: string): boolean {
    const user = this.getCurrentUser();
    return user?.roles?.includes(role) || false;
  }

  /**
   * Check if user requires division filtering
   * Gets the value from the permission service which loads it from /me/permissions
   */
  requiresDivisionFiltering(): boolean {
    return this.permissionService.getRequiresDivisionFiltering();
  }

  /**
   * Check if user has admin role
   */
  isAdmin(): boolean {
    return this.hasRole('admin');
  }

  /**
   * Check if currently impersonating another user
   */
  isImpersonating(): boolean {
    return localStorage.getItem('isImpersonating') === 'true';
  }

  /**
   * Check if user has any of the specified roles
   */
  hasAnyRole(roles: string[]): boolean {
    const user = this.getCurrentUser();
    const userRoles = user?.roles || [];
    return roles.some(role => userRoles.includes(role));
  }

  getHomeUrl(): string {
    // Check specific roles in order of priority
    if (this.hasRole('admin')) {
      return '/dashboards/holdco-revenue';
    } else if (this.hasRole('sparq_fp&a')) {
      return '/dashboards/holdco-revenue';
    } else if (this.hasRole('sparq_staff')) {
      return '/dashboards/holdco-revenue';
    } else if (this.hasRole('sparq_intern')) {
      return '/dashboards/holdco-revenue';
    } else if (this.hasRole('production_leadership')) {
      return '/dashboards/ep-main';
    } else if (this.hasRole('production_team') || this.hasRole('production_external')) {
      // Unbilled is the only page either production role can reach (SPAR-742 narrowed the
      // internal one to match the external one, so they share a landing page). This entry is
      // not cosmetic: RoleGuard sends every denial here, and the '' route redirects here on
      // load, so a role missing from this chain falls through to '/' — which is that same
      // redirect component, i.e. an infinite loop.
      return '/invoices/unbilled-projects';
    } else if (this.hasRole('accounts_sales_team')) {
      return '/dashboards/ep-main';
    } else if (this.hasRole('division_executive')) {
      return '/dashboards/holdco-revenue';
    } else if (this.hasRole('reporting_entity_executive')) {
      return '/dashboards/holdco-revenue';
    } else if (this.hasRole('founder')) {
      return '/dashboards/holdco-revenue';
    } else {
      return '/';
    }
  }

  /**
   * Validates that a return URL is safe (internal) to prevent open redirect attacks
   * @param returnUrl The URL to validate
   * @returns true if the URL is safe, false otherwise
   */
  isValidReturnUrl(returnUrl: string): boolean {
    if (!returnUrl) {
      return false;
    }

    // Must be a relative URL starting with /
    if (!returnUrl.startsWith('/')) {
      return false;
    }

    // Block protocol handlers and double slashes that could bypass validation
    if (returnUrl.startsWith('//') || returnUrl.includes('://')) {
      return false;
    }

    // Block javascript: and data: URLs
    if (returnUrl.toLowerCase().startsWith('javascript:') || returnUrl.toLowerCase().startsWith('data:')) {
      return false;
    }

    return true;
  }

  getQuickBooksAuthUrl(): Observable<string> {
    return this.http.get<{authorizationUri: string}>(`${environment.apiUrl}/intuit/auth`)
        .pipe(map(response => {
            console.log("getQuickBooksAuthUrl response:", response);
            return response.authorizationUri;
        }));
  }

  exchangeCodeForToken(code: string) {
    return this.http.post(`${environment.apiUrl}/intuit/callback`, { code });
  }

  getUserProfile(): Observable<{user: User}> {
    return this.http.get<{user: User}>(`${environment.apiUrl}/users/me`);
  }
}
